// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useGroupDetailQuery, useUpdateGroupMutation } from "@/api/hooks/use-groups";
import { SaveDiscardPane } from "@/components/shared/save-discard-pane";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  FULL_ACCESS,
  KNOWN_PRODUCT_PERMISSIONS,
  normalizeProduct,
  PERMISSION_DESCRIPTIONS,
  productPermissionMatches,
  productPermissionSetsEqual,
  REGULAR_SECTIONS,
} from "@/features/iam/permission-trees";
import { cn } from "@/lib/utils";

export interface GroupPermissionsTabHandle {
  reset: () => void;
}

interface GroupPermissionsTabProps {
  groupName: string;
  canEdit: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  ref?: React.Ref<GroupPermissionsTabHandle>;
}

export function GroupPermissionsTab({ groupName, canEdit, onDirtyChange, ref }: GroupPermissionsTabProps) {
  const { t } = useTranslation("back-office");
  const { data } = useGroupDetailQuery(groupName);
  const updateGroup = useUpdateGroupMutation(groupName);

  // The group's full flat permission set.
  const allPermissions = useMemo(() => data?.permissions ?? [], [data]);

  // The slice this tab manages: the product-permission domain (away from `backoffice:` BO perms).
  const managedPermissions = useMemo(() => {
    const set = new Set<string>();
    for (const permission of allPermissions) {
      const normalized = normalizeProduct(permission);
      if (KNOWN_PRODUCT_PERMISSIONS.has(normalized)) set.add(normalized);
    }
    return set;
  }, [allPermissions]);

  // Baseline the dirty check compares against. Tracks the persisted state, advanced on a successful
  // save so the form clears immediately — without waiting for the refetched data.
  const [baseline, setBaseline] = useState<Set<string>>(() => new Set(managedPermissions));
  const [enabled, setEnabled] = useState<Set<string>>(() => new Set(managedPermissions));

  useEffect(
    function resetWhenDataLoads() {
      setEnabled(new Set(managedPermissions));
      setBaseline(new Set(managedPermissions));
    },
    [managedPermissions],
  );

  const isDirty = !productPermissionSetsEqual(enabled, baseline);

  useImperativeHandle(ref, () => ({ reset: () => setEnabled(new Set(baseline)) }));

  const onDirtyChangeRef = useRef(onDirtyChange);
  onDirtyChangeRef.current = onDirtyChange;
  useEffect(
    function notifyParentOfDirtyState() {
      onDirtyChangeRef.current?.(isDirty);
    },
    [isDirty],
  );

  // A broader enabled permission that already covers `required`.
  function coveredByOther(required: string): string | undefined {
    for (const granted of enabled) {
      if (granted !== required && productPermissionMatches(granted, required)) return granted;
    }
    return undefined;
  }

  function toggle(permission: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(permission)) next.delete(permission);
      else next.add(permission);
      return next;
    });
  }

  function renderSwitch(permission: string) {
    const covered = coveredByOther(permission);
    if (covered) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center text-muted-foreground">
              <Switch checked disabled aria-readonly className="pointer-events-none opacity-60" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="font-mono text-xs">
            {t("users.permissions.grantedVia", { permission: covered })}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <Switch checked={enabled.has(permission)} disabled={!canEdit} onCheckedChange={() => toggle(permission)} />;
  }

  async function onSave() {
    if (!data) return;
    // Resend the full permission set: the slices this tab doesn't manage (the BO domain & unknown
    // grants, preserved as-is) plus the switches enabled here.
    const preserved = allPermissions.filter(
      (permission) => !KNOWN_PRODUCT_PERMISSIONS.has(normalizeProduct(permission)),
    );
    const permissions = [...new Set([...preserved, ...enabled])];
    try {
      await updateGroup.mutateAsync({ permissions });
      setBaseline(new Set(enabled));
      toast.success(t("groups.notifications.updated", { name: data.group.name }));
    } catch {
      // Error toast handled by API middleware
    }
  }

  function gridRow(
    key: string,
    label: React.ReactNode,
    permission: string,
    variant?: "section" | "node",
    separator?: boolean,
    description?: string,
  ) {
    const labelClass = variant === "section" ? "font-medium" : variant === "node" ? "font-mono text-xs" : undefined;
    return (
      <Fragment key={key}>
        <div className={cn("px-3 py-2", variant === "node" && "pl-6", separator && "border-b")}>
          {description ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-4",
                    labelClass,
                  )}
                >
                  {label}
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">{description}</TooltipContent>
            </Tooltip>
          ) : (
            <span className={labelClass}>{label}</span>
          )}
        </div>
        <div className="min-h-9 justify-center py-1.5">{renderSwitch(permission)}</div>
      </Fragment>
    );
  }

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div
        className={cn(
          "grid grid-cols-[1fr_3.5rem] text-sm",
          "[&>*]:flex [&>*]:items-center",
          "[&>*:nth-child(2n)]:border-x [&>*:nth-child(2n)]:border-b",
          "[&>*:nth-child(2)]:border-t",
        )}
      >
        {gridRow(
          "__full-access",
          t("users.permissions.fullAccess"),
          FULL_ACCESS,
          "section",
          true,
          t("users.permissions.everyPermission"),
        )}

        {REGULAR_SECTIONS.map((section, sectionIndex) => {
          const isLastSection = sectionIndex === REGULAR_SECTIONS.length - 1;
          return (
            <Fragment key={section.prefix}>
              {gridRow(
                `${section.prefix}__header`,
                t(section.label),
                `${section.prefix}:*`,
                "section",
                // A wildcard-only section has no rows to carry the divider, so its header does.
                !isLastSection && section.permissions.length === 0,
                t("users.permissions.allPermissions", { section: t(section.label) }),
              )}
              {section.permissions.map((permission, permissionIndex) =>
                gridRow(
                  permission,
                  permission,
                  permission,
                  "node",
                  !isLastSection && permissionIndex === section.permissions.length - 1,
                  PERMISSION_DESCRIPTIONS[permission],
                ),
              )}
            </Fragment>
          );
        })}
      </div>

      {canEdit && isDirty && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          <SaveDiscardPane onDiscard={() => setEnabled(new Set(baseline))} saving={updateGroup.isPending} />
        </form>
      )}
    </div>
  );
}
